import { useState } from 'react';
import Footer from '../home/components/Footer';

export default function PublicationsPage() {
  const [showDownloadForm, setShowDownloadForm] = useState(false);
  const [selectedPublication, setSelectedPublication] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    publication: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Video transcript states
  const [showTranscript, setShowTranscript] = useState<{ [key: string]: boolean }>({});
  const [transcriptUnlocked, setTranscriptUnlocked] = useState<{ [key: string]: boolean }>({});
  const [transcriptFormData, setTranscriptFormData] = useState({ name: '', email: '' });
  const [transcriptSearch, setTranscriptSearch] = useState('');

  const publications = [
    {
      id: 'design-defect',
      title: 'From Design Defect to Constitutional Design: A Legal Framework for Trustworthy AI',
      description: 'Legal framework addressing how Constitutional AI addresses design defects in high-stakes AI systems. Essential reading for NIST AI RMF officials, policymakers, and AI risk engineers.',
      date: 'December 2025',
      type: 'White Paper',
      link: '/design-defect-constitutional-design',
      downloadable: true,
      featured: true
    },
    {
      id: 'luevano-standard',
      title: 'The Luevano Standard: California\'s Blueprint for High-Trust AI',
      description: 'A comprehensive legal framework establishing strict liability for opaque AI systems and safe harbor for Constitutional AI. Proposes the California Algorithmic Accountability & Fairness Act (CA AAFA) to end algorithmic discrimination.',
      date: 'January 2025',
      type: 'White Paper',
      link: '/luevano-standard',
      downloadable: true,
      featured: false
    },
    {
      id: 'survival-imperative',
      title: 'The Survival Imperative: Why AI Safety Cannot Wait',
      description: 'An urgent call to action examining why immediate implementation of Constitutional AI principles is critical for preventing catastrophic AI failures.',
      date: 'January 2025',
      type: 'White Paper',
      link: '/survival-imperative',
      downloadable: true
    },
    {
      id: 'whitepaper',
      title: 'The Mirror Effect: How AI Systems Amplify Historical Bias',
      description: 'Exploring how AI systems trained on historical data inevitably reflect and amplify societal biases, and why Constitutional AI is necessary to break this cycle.',
      date: 'January 2025',
      type: 'White Paper',
      link: '/whitepaper',
      downloadable: true
    },
    {
      id: 'state-of-ai-2025',
      title: 'The State of AI in 2025: Acceleration, Integration, and Responsibility',
      description: 'A comprehensive analysis of AI\'s current capabilities, challenges, and societal impact as we navigate the transformative year of 2025.',
      date: 'January 2025',
      type: 'White Paper',
      link: '/state-of-ai-2025',
      downloadable: true
    },
    {
      id: 'ai-liability',
      title: 'AI Liability Framework: Legal Accountability in the Age of Autonomous Systems',
      description: 'A comprehensive framework for understanding and addressing liability issues in AI deployment and decision-making.',
      date: 'December 2024',
      type: 'Research Paper',
      link: '/ai-liability',
      downloadable: true
    },
    {
      id: 'constitutional-ai',
      title: 'Constitutional AI: Engineering Trust Through Principled Design',
      description: 'Exploring how constitutional frameworks can guide AI development to ensure alignment with human values and democratic principles.',
      date: 'December 2024',
      type: 'White Paper',
      link: '/constitutional-ai',
      downloadable: true
    },
    {
      id: 'ai-safety-bridge',
      title: 'The Future of AI Safety: Bridging the Gap Between Technical Alignment and Risk Engineering',
      description: 'Examining the critical gap between technical AI alignment and practical risk engineering, and how Constitutional AI provides the necessary bridge.',
      date: 'December 2024',
      type: 'White Paper',
      link: '/ai-safety-bridge',
      downloadable: true
    },
    {
      id: 'cai-standard',
      title: 'The Future of AI Safety: CAI Engineering Standard',
      description: 'Technical standards and best practices for implementing Constitutional AI principles in production systems.',
      date: 'November 2024',
      type: 'Technical Standard',
      link: '/cai-engineering-standard',
      downloadable: true
    },
    {
      id: 'engineering-trust',
      title: 'Engineering Trust: Implementing Constitutional AI in Practice',
      description: 'Methodologies and frameworks for building trustworthy AI systems that maintain human oversight and accountability.',
      date: 'November 2024',
      type: 'Research Paper',
      link: '/engineering-trust',
      downloadable: true
    }
  ];

  // Video 10 Transcript Data
  const video10Transcript = [
    { time: '0:00', speaker: 'Speaker 1', text: 'Imagine you\'re applying for a job, a loan, or even just trying to get into college.' },
    { time: '0:05', speaker: 'Speaker 1', text: 'And somewhere in the background, an AI system is quietly making a decision about your future.' },
    { time: '0:10', speaker: 'Speaker 1', text: 'You don\'t see it, you don\'t question it, and honestly, you might not even know it\'s there.' },
    { time: '0:16', speaker: 'Speaker 1', text: 'But here\'s the thing, that AI might be discriminating against you, and the people who built it might not even realize it.' },
    { time: '0:24', speaker: 'Speaker 1', text: 'Welcome to the world of black box AI, where decisions are made, but nobody can really explain why.' },
    { time: '0:31', speaker: 'Speaker 1', text: 'Today, we\'re going to break down a legal framework that could actually change all of this.' },
    { time: '0:36', speaker: 'Speaker 1', text: 'It\'s called from design defect to constitutional design, and it might just be the blueprint for building AI we can actually trust.' },
    { time: '0:44', speaker: 'Speaker 1', text: 'So let\'s start with the big problem.' },
    { time: '0:46', speaker: 'Speaker 1', text: 'AI has become the new invisible gatekeeper in so many parts of our lives.' },
    { time: '0:51', speaker: 'Speaker 1', text: 'It\'s deciding who gets hired, who gets a loan, who even gets released on bail.' },
    { time: '0:56', speaker: 'Speaker 1', text: 'And the issue is that a lot of these AI systems, they\'re what we call black boxes.' },
    { time: '1:01', speaker: 'Speaker 1', text: 'You put data in, you get a decision out, but what happens in the middle, that\'s a total mystery.' },
    { time: '1:08', speaker: 'Speaker 1', text: 'And when these systems are trained on historical data, which let\'s be honest, is full of human bias, they end up baking that bias right into their decisions.' },
    { time: '1:17', speaker: 'Speaker 1', text: 'It\'s like teaching someone to drive by only showing them accidents.' },
    { time: '1:21', speaker: 'Speaker 1', text: 'They\'re going to learn the wrong lessons.' },
    { time: '1:24', speaker: 'Speaker 1', text: 'Now, if this sounds abstract, let me give you a real world example that makes it super concrete.' },
    { time: '1:30', speaker: 'Speaker 1', text: 'There was this exam called the PACE exam.' },
    { time: '1:33', speaker: 'Speaker 1', text: 'It was used for decades to screen people for entry level government jobs.' },
    { time: '1:38', speaker: 'Speaker 1', text: 'And on the surface, it seemed totally neutral, right?' },
    { time: '1:41', speaker: 'Speaker 1', text: 'It was just a test.' },
    { time: '1:42', speaker: 'Speaker 1', text: 'But here\'s what happened.' },
    { time: '1:44', speaker: 'Speaker 1', text: 'It turned out that the test had a massive disparate impact.' },
    { time: '1:48', speaker: 'Speaker 1', text: 'It was filtering out minority candidates at a much higher rate than white candidates.' },
    { time: '1:53', speaker: 'Speaker 1', text: 'And when this came to light, the government had to stop using it.' },
    { time: '1:57', speaker: 'Speaker 1', text: 'They even had to pay out damages.' },
    { time: '1:59', speaker: 'Speaker 1', text: 'The key point here, nobody designed the PACE exam to be discriminatory.' },
    { time: '2:04', speaker: 'Speaker 1', text: 'There was no malicious intent.' },
    { time: '2:06', speaker: 'Speaker 1', text: 'But the impact was still illegal because the harm was foreseeable and there were better alternatives available.' },
    { time: '2:14', speaker: 'Speaker 1', text: 'So here\'s the big question.' },
    { time: '2:16', speaker: 'Speaker 1', text: 'If we can hold a paper and pencil test accountable for discriminatory outcomes, why can\'t we do the same thing for AI?' },
    { time: '2:24', speaker: 'Speaker 1', text: 'And that brings us to the core argument of this framework.' },
    { time: '2:28', speaker: 'Speaker 1', text: 'We should start thinking about biased AI not just as a bug or an accident, but as a design defect.' },
    { time: '2:38', speaker: 'Speaker 1', text: 'Here\'s the thing.' },
    { time: '2:39', speaker: 'Speaker 1', text: 'When you build an AI system, you\'re making a whole bunch of choices along the way.' },
    { time: '2:44', speaker: 'Speaker 1', text: 'What data do you use?' },
    { time: '2:45', speaker: 'Speaker 1', text: 'What model architecture do you pick?' },
    { time: '2:48', speaker: 'Speaker 1', text: 'Do you build in any transparency or explainability?' },
    { time: '2:52', speaker: 'Speaker 1', text: 'And just like with any other product, those choices can have defects.' },
    { time: '2:58', speaker: 'Speaker 1', text: 'Now to really get this, we need two key legal ideas.' },
    { time: '3:02', speaker: 'Speaker 1', text: 'First up is disparate impact.' },
    { time: '3:05', speaker: 'Speaker 1', text: 'This comes straight out of civil rights law, and it basically says that a practice can be illegal if it harms a certain group, even if nobody meant for it to be discriminatory.' },
    { time: '3:15', speaker: 'Speaker 1', text: 'It\'s the effect that matters, not the intent.' },
    { time: '3:18', speaker: 'Speaker 1', text: 'The second idea is design defect, and this one comes from product liability.' },
    { time: '3:23', speaker: 'Speaker 1', text: 'Think about cars.' },
    { time: '3:24', speaker: 'Speaker 1', text: 'The moment airbags became a feasible safer alternative, building a car without them could suddenly be considered a design defect.' },
    { time: '3:31', speaker: 'Speaker 1', text: 'It\'s the same logic we can apply to AI.' },
    { time: '3:34', speaker: 'Speaker 1', text: 'The legal test here is actually pretty straightforward.' },
    { time: '3:37', speaker: 'Speaker 1', text: 'One, was there a safer alternative design?' },
    { time: '3:40', speaker: 'Speaker 1', text: 'Two, would that alternative have cut down on a foreseeable risk, like, say, discrimination?' },
    { time: '3:45', speaker: 'Speaker 1', text: 'And three, was that alternative actually practical to build?' },
    { time: '3:48', speaker: 'Speaker 1', text: 'If the answer to all those is yes, then you just might be looking at a design defect.' },
    { time: '3:53', speaker: 'Speaker 1', text: 'So when you put these two big ideas together, you get this incredibly powerful new argument.' },
    { time: '3:58', speaker: 'Speaker 1', text: 'The choice to use an opaque black box AI for something as critical as hiring, that\'s a design choice.' },
    { time: '4:05', speaker: 'Speaker 1', text: 'And given everything we know, the risk of it violating civil rights is completely foreseeable.' },
    { time: '4:10', speaker: 'Speaker 1', text: 'So the question becomes, is there a safer alternative out there?' },
    { time: '4:14', speaker: 'Speaker 1', text: 'And the answer is yes, there is, which brings us to the solution on the table, a paradigm called constitutional AI.' },
    { time: '4:21', speaker: 'Speaker 1', text: 'It is a totally different way to build these systems right from the ground out.' },
    { time: '4:24', speaker: 'Speaker 1', text: 'And, wow, the contrast could not be more clear.' },
    { time: '4:27', speaker: 'Speaker 1', text: 'A black box AI just learns from whatever historical data you feed it, biases and all, and its logic is a complete mystery.' },
    { time: '4:35', speaker: 'Speaker 1', text: 'Constitutional AI, on the other hand, is guided by an explicit written constitution, a set of rules based on actual laws and policies.' },
    { time: '4:43', speaker: 'Speaker 1', text: 'It doesn\'t just spit out a decision, it gives you an audible reason why.' },
    { time: '4:48', speaker: 'Speaker 1', text: 'It all comes down to three main pillars.' },
    { time: '4:51', speaker: 'Speaker 1', text: 'First, you have the constitution, those written rules.' },
    { time: '4:55', speaker: 'Speaker 1', text: 'Second, you get reasoning traces, a clear explanation, a paper trail for every single important decision.' },
    { time: '5:02', speaker: 'Speaker 1', text: 'And third, a constitutional error rate or CSR for short.' },
    { time: '5:06', speaker: 'Speaker 1', text: 'This is like a built in alarm system constantly measuring how often the AI is breaking its own rules.' },
    { time: '5:13', speaker: 'Speaker 1', text: 'And let\'s be really clear here.' },
    { time: '5:15', speaker: 'Speaker 1', text: 'This isn\'t science fiction.' },
    { time: '5:16', speaker: 'Speaker 1', text: 'This isn\'t some pie in the sky idea.' },
    { time: '5:19', speaker: 'Speaker 1', text: 'This is an engineering pattern that we can build right now with the technology we already have.' },
    { time: '5:24', speaker: 'Speaker 1', text: 'It\'s all about making different and better design choices.' },
    { time: '5:29', speaker: 'Speaker 1', text: 'Okay.' },
    { time: '5:29', speaker: 'Speaker 1', text: 'So if the tech is here, how do we make this the new standard?' },
    { time: '5:32', speaker: 'Speaker 1', text: 'Well, that leads us to our final section, the actual road map for building AI we can trust.' },
    { time: '5:37', speaker: 'Speaker 2', text: 'It\'s gonna take a three pronged approach.' },
    { time: '5:40', speaker: 'Speaker 2', text: 'The courts can start applying these design defect principles to AI cases.' },
    { time: '5:44', speaker: 'Speaker 2', text: 'Regulators can create frameworks to gently guide companies toward these safer designs, and companies, well, they need to start treating AI risk as a serious board level responsibility, not just some problem for the IT department.' },
    { time: '6:00', speaker: 'Speaker 1', text: 'One really important tool for regulators could be something called an algorithmic compliance agreement.' },
    { time: '6:05', speaker: 'Speaker 1', text: 'Think of it like a deal.' },
    { time: '6:06', speaker: 'Speaker 1', text: 'A company agrees to build its AI the constitutional way and to report on its performance.' },
    { time: '6:11', speaker: 'Speaker 1', text: 'In return, the regulator gives them clearer expectations and a much more predictable environment to work in.' },
    { time: '6:17', speaker: 'Speaker 1', text: 'Now, of course, this isn\'t free.' },
    { time: '6:19', speaker: 'Speaker 1', text: 'Building a constitutional AI takes an upfront investment.' },
    { time: '6:23', speaker: 'Speaker 1', text: 'But here\'s the thing, that investment is tiny compared to the enormous long term risk of sticking with black box models.' },
    { time: '6:29', speaker: 'Speaker 1', text: 'We\'re talking about fines, lawsuits, and the catastrophic loss of public trust when, not if, things go wrong.' },
    { time: '6:36', speaker: 'Speaker 1', text: 'At the end of the day, this is where we are.' },
    { time: '6:38', speaker: 'Speaker 1', text: 'Regulation is coming one way or another.' },
    { time: '6:40', speaker: 'Speaker 1', text: 'The choice we have is how we do it.' },
    { time: '6:42', speaker: 'Speaker 1', text: 'We can either wait for a disaster and then react with a bunch of messy rules, or we can proactively adopt a smart, coherent framework like this one.' },
    { time: '6:51', speaker: 'Speaker 1', text: 'And that brings us to the final question, really, the one for all of us.' },
    { time: '6:55', speaker: 'Speaker 1', text: 'What kind of future are we designing right now?' },
    { time: '6:57', speaker: 'Speaker 1', text: 'Are we building a world full of black boxes with endless liability and uncertainty?' },
    { time: '7:02', speaker: 'Speaker 1', text: 'Or can we build glass boxes that operate with transparency and can actually earn our trust?' },
    { time: '7:08', speaker: 'Speaker 1', text: 'The choice and all the design work that comes with it is up to us.' }
  ];

  const video10KeyTopics = [
    { title: 'AI as the New Gatekeeper', time: '0:00-0:44' },
    { title: 'The PACE Exam Historical Parallel', time: '0:44-1:42' },
    { title: 'Framing Biased AI as Design Defect', time: '1:42-2:38' },
    { title: 'Disparate Impact & Design Defect', time: '2:38-3:33' },
    { title: 'Constitutional AI Solution', time: '3:33-4:53' },
    { title: 'Three-Pronged Roadmap', time: '4:53-5:40' },
    { title: 'Algorithmic Compliance Agreements', time: '5:40-6:16' },
    { title: 'The Final Question', time: '6:16-6:48' }
  ];

  const handleDownloadClick = (publicationId: string, publicationTitle: string) => {
    setSelectedPublication(publicationId);
    setFormData({ ...formData, publication: publicationTitle });
    setShowDownloadForm(true);
    setSubmitSuccess(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        organization: formData.organization,
        publication: formData.publication
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d4nsolajh8g73q73a06g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(() => {
          window.open(`${selectedPublication}`, '_blank');
        }, 1500);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowDownloadForm(false);
    setFormData({ name: '', email: '', organization: '', publication: '' });
    setSubmitSuccess(false);
  };

  const handleTranscriptUnlock = (videoId: string, e: React.FormEvent) => {
    e.preventDefault();
    if (transcriptFormData.name && transcriptFormData.email) {
      setTranscriptUnlocked({ ...transcriptUnlocked, [videoId]: true });
    }
  };

  const filteredTranscript = video10Transcript.filter(entry =>
    entry.text.toLowerCase().includes(transcriptSearch.toLowerCase()) ||
    entry.speaker.toLowerCase().includes(transcriptSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Publications & Research
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            Access our latest white papers, research findings, and technical standards on AI governance, safety, and responsible innovation.
          </p>
          
          {/* New Transcripts Link */}
          <a
            href="/transcripts"
            className="inline-flex items-center gap-2 px-6 py-3 bg-red-800 text-white font-semibold hover:bg-red-900 transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
          >
            <i className="ri-file-text-line text-xl"></i>
            Browse All Transcripts
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </section>

      {/* Featured Publication - Executive Summary */}
      <div className="mb-16 bg-red-50 border-2 border-red-800 rounded-lg p-8 shadow-lg max-w-6xl mx-auto px-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="flex-shrink-0 w-12 h-12 bg-red-800 text-white flex items-center justify-center rounded-lg">
            <i className="ri-star-fill text-2xl"></i>
          </div>
          <div className="flex-1">
            <div className="inline-block bg-red-800 text-white px-3 py-1 text-xs font-bold mb-3 whitespace-nowrap">
              EXECUTIVE SUMMARY
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              <a href="/playbook-executive-summary" className="hover:text-red-800 transition-colors cursor-pointer">
                The Constitutional AI Implementation Playbook
              </a>
            </h3>
            <p className="text-lg text-slate-700 mb-4">
              Operationalizing the "Third Way": From Black Box Liability to Glass Box Trust. A concise 5-phase engineering framework to embed safety norms directly into AI models.
            </p>
            <a 
              href="/playbook-executive-summary" 
              className="inline-flex items-center gap-2 bg-red-800 hover:bg-red-900 text-white px-6 py-3 font-semibold transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
            >
              Read Executive Summary
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Video Section - NEW */}
      <section className="py-16 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Video Presentations</h2>
            <p className="text-lg text-gray-600">Watch our latest video presentations with full transcripts</p>
          </div>

          {/* Video 10 Card */}
          <div id="video-10" className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-semibold rounded-full">
                  <i className="ri-video-line mr-1"></i>
                  Video Presentation
                </span>
                <span className="text-sm text-gray-500">6:48</span>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                AI Governance Framework Implementation
              </h3>

              <p className="text-gray-600 mb-6 leading-relaxed">
                A comprehensive discussion on implementing Constitutional AI frameworks, covering design defects in AI systems, disparate impact, and the roadmap for building trustworthy AI through glass box transparency.
              </p>

              {/* View Transcript Button */}
              <button
                onClick={() => setShowTranscript({ ...showTranscript, 'video-10': !showTranscript['video-10'] })}
                className="inline-flex items-center gap-2 px-6 py-3 bg-red-800 text-white font-semibold hover:bg-red-900 transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-text-line"></i>
                {showTranscript['video-10'] ? 'Hide Transcript' : 'View Full Transcript'}
                <i className={`ri-arrow-${showTranscript['video-10'] ? 'up' : 'down'}-line`}></i>
              </button>
            </div>

            {/* Transcript Section */}
            {showTranscript['video-10'] && (
              <div className="border-t border-gray-200 bg-gray-50 p-8">
                {!transcriptUnlocked['video-10'] ? (
                  <div className="max-w-2xl mx-auto">
                    {/* Key Topics - Show BEFORE unlock */}
                    <div className="mb-6 bg-white rounded-lg p-6 shadow-md">
                      <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-list-check text-red-800"></i>
                        Key Topics Covered
                      </h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {video10KeyTopics.map((topic, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm">
                            <i className="ri-arrow-right-s-line text-red-800 mt-0.5"></i>
                            <div>
                              <span className="font-medium text-gray-900">{topic.title}</span>
                              <span className="text-gray-500 ml-2">({topic.time})</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-8 shadow-md">
                      <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="ri-lock-line text-3xl text-red-800"></i>
                        </div>
                        <h4 className="text-2xl font-bold text-gray-900 mb-2">
                          Unlock Full Transcript
                        </h4>
                        <p className="text-gray-600">
                          Enter your details to access the complete transcript with timestamps and search functionality
                        </p>
                      </div>

                      {/* Preview */}
                      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm font-semibold text-gray-700 mb-3">Preview (First 8 entries):</p>
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                          {video10Transcript.slice(0, 8).map((entry, idx) => (
                            <div key={idx} className="text-sm">
                              <span className="font-semibold text-red-800">[{entry.time}]</span>
                              <span className="text-gray-600 ml-2">{entry.speaker}:</span>
                              <span className="text-gray-800 ml-2">{entry.text}</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-3 text-center">
                          ...and {video10Transcript.length - 8} more entries
                        </p>
                      </div>

                      <form onSubmit={(e) => handleTranscriptUnlock('video-10', e)} className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Name *
                          </label>
                          <input
                            type="text"
                            required
                            value={transcriptFormData.name}
                            onChange={(e) => setTranscriptFormData({ ...transcriptFormData, name: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-800 focus:border-transparent text-sm"
                            placeholder="Your name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email *
                          </label>
                          <input
                            type="email"
                            required
                            value={transcriptFormData.email}
                            onChange={(e) => setTranscriptFormData({ ...transcriptFormData, email: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-800 focus:border-transparent text-sm"
                            placeholder="your@email.com"
                          />
                        </div>
                        <button
                          type="submit"
                          className="w-full px-6 py-3 bg-red-800 text-white font-semibold hover:bg-red-900 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Unlock Transcript
                        </button>
                      </form>
                    </div>
                  </div>
                ) : (
                  <div>
                    {/* Table of Contents */}
                    <div className="mb-8 bg-white rounded-lg p-6 shadow-md">
                      <h4 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-list-check text-red-800"></i>
                        Table of Contents
                      </h4>
                      
                      {/* Key Topics Covered */}
                      <div className="mb-6">
                        <h5 className="font-semibold text-gray-800 mb-3">Key Topics Covered:</h5>
                        <div className="grid md:grid-cols-2 gap-3">
                          {video10KeyTopics.map((topic, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-sm">
                              <i className="ri-arrow-right-s-line text-red-800 mt-0.5"></i>
                              <div>
                                <span className="font-medium text-gray-900">{topic.title}</span>
                                <span className="text-gray-500 ml-2">({topic.time})</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-6 text-sm text-gray-600 pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-2">
                          <i className="ri-time-line text-red-800"></i>
                          <span>Duration: 6:48</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-chat-3-line text-red-800"></i>
                          <span>{video10Transcript.length} entries</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-user-voice-line text-red-800"></i>
                          <span>2 speakers</span>
                        </div>
                      </div>
                    </div>

                    {/* Search */}
                    <div className="mb-6">
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                          <i className="ri-search-line text-xl text-gray-400"></i>
                        </div>
                        <input
                          type="text"
                          placeholder="Search transcript..."
                          value={transcriptSearch}
                          onChange={(e) => setTranscriptSearch(e.target.value)}
                          className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-800 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    {/* Full Transcript */}
                    <div className="bg-white rounded-lg p-6 shadow-md">
                      <h4 className="text-xl font-bold text-gray-900 mb-4">Full Transcript</h4>
                      <div className="space-y-4 max-h-[600px] overflow-y-auto">
                        {filteredTranscript.map((entry, idx) => (
                          <div key={idx} className="pb-4 border-b border-gray-100 last:border-0">
                            <div className="flex items-start gap-3">
                              <span className="font-semibold text-red-800 text-sm whitespace-nowrap">
                                [{entry.time}]
                              </span>
                              <div className="flex-1">
                                <span className="font-medium text-gray-700 text-sm">{entry.speaker}:</span>
                                <p className="text-gray-800 mt-1">{entry.text}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {transcriptSearch && filteredTranscript.length === 0 && (
                        <p className="text-center text-gray-500 py-8">No results found for "{transcriptSearch}"</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Publications Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {publications.map((pub) => (
              <article key={pub.id} className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-teal-100 text-teal-700 text-sm font-medium rounded-full">
                    {pub.type}
                  </span>
                  <span className="text-sm text-gray-500">{pub.date}</span>
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {pub.title}
                </h2>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {pub.description}
                </p>
                
                <div className="flex gap-4">
                  <a
                    href={pub.link}
                    className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Read Online
                  </a>
                  {pub.downloadable && (
                    <button
                      onClick={() => handleDownloadClick(pub.link, pub.title)}
                      className="px-6 py-3 border-2 border-teal-600 text-teal-600 rounded-lg hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Download Free
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Download Form Modal */}
      {showDownloadForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-lg max-w-md w-full p-8 relative">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            {!submitSuccess ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-download-cloud-line text-3xl text-teal-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Download White Paper
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Please provide your details to access this publication
                  </p>
                </div>

                <form id="whitepaper-download-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                      Organization
                    </label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Your Company"
                    />
                  </div>

                  <input type="hidden" name="publication" value={formData.publication} />

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Processing...' : 'Download Now'}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    We respect your privacy. Your information will only be used to send you relevant updates.
                  </p>
                </form>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Thank You!
                </h3>
                <p className="text-gray-600">
                  Your download will begin shortly...
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Stay Updated on Our Latest Research
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Subscribe to receive notifications when we publish new white papers and research findings.
          </p>
          <a
            href="/#contact"
            className="inline-block px-8 py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
          >
            Subscribe to Updates
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
